import React, { useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { useAgentTraces } from '../../hooks/useAgentAnalytics';
import type {
  AnalyticsDateRange,
  AnalyticsPeriod,
  AgentTraceSummary,
} from '../../services/agentAnalyticsApi';
import { TraceDetailDrawer } from './TraceDetailDrawer';

interface RecentTracesTableProps {
  agentId: string;
  period: AnalyticsPeriod;
  /** Intervalo personalizado — substitui a janela do `period` */
  range?: AnalyticsDateRange;
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function scoreColor(score: number | null): string {
  if (score === null) return '#6b7280';
  if (score >= 0.8) return '#10b981';
  if (score >= 0.5) return '#f59e0b';
  return '#ef4444';
}

export const RecentTracesTable: React.FC<RecentTracesTableProps> = ({
  agentId,
  period,
  range,
}) => {
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const { data, isLoading } = useAgentTraces(agentId, period, { limit: 20 }, range);

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div
        className="px-4 py-3 border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <h3
          className="text-sm font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          Conversas recentes
        </h3>
      </div>

      {isLoading && (
        <div
          className="p-4 text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Carregando...
        </div>
      )}

      {!isLoading && data && data.items.length === 0 && (
        <div
          className="p-4 text-sm"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          Nenhuma conversa registrada no período.
        </div>
      )}

      {!isLoading && data && data.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                style={{
                  backgroundColor: 'var(--theme-page-background)',
                  borderBottom: '1px solid var(--theme-card-border)',
                }}
              >
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Quando
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Resposta
                </th>
                <th
                  className="text-right px-4 py-2 font-medium"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Score
                </th>
                <th
                  className="text-right px-4 py-2 font-medium"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Latência
                </th>
                <th
                  className="text-left px-4 py-2 font-medium"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Modelo
                </th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((trace: AgentTraceSummary, idx: number) => (
                <tr
                  key={trace.traceId}
                  className="cursor-pointer transition-colors hover:bg-black/5"
                  style={{
                    borderTop:
                      idx > 0 ? '1px solid var(--theme-card-border)' : undefined,
                  }}
                  onClick={() => setSelectedTraceId(trace.traceId)}
                >
                  <td
                    className="px-4 py-2 whitespace-nowrap"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {formatTimestamp(trace.timestamp)}
                  </td>
                  <td
                    className="px-4 py-2 max-w-md truncate"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {trace.outputSnippet || <em>(sem resposta)</em>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span
                      className="font-medium"
                      style={{ color: scoreColor(trace.averageScore) }}
                    >
                      {trace.averageScore !== null
                        ? `${Math.round(trace.averageScore * 100)}%`
                        : 'Pendente'}
                    </span>
                  </td>
                  <td
                    className="px-4 py-2 text-right"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {trace.latencySeconds.toFixed(1)}s
                  </td>
                  <td
                    className="px-4 py-2 text-left"
                    style={{ color: 'var(--theme-muted-text)', fontSize: '12px' }}
                  >
                    {trace.model || '—'}
                  </td>
                  <td className="px-2 py-2">
                    <FiExternalLink
                      className="h-4 w-4"
                      style={{ color: 'var(--theme-muted-text)' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TraceDetailDrawer
        agentId={agentId}
        traceId={selectedTraceId}
        onClose={() => setSelectedTraceId(null)}
      />
    </div>
  );
};
